import { Alert, Button, Dialog, DialogContent } from "@mui/material";
import { t } from "i18next";
import React, { useState } from "react";
import { BankType, RenderBankLogo } from "./PaymentInformation";
import { colorPrimary } from "../../rsc/colors/colors";
import { isArrayNullOrEmpty, isNullOrEmpty, isNullOrUndefined, showErrorMessage, showSuccessMessage } from "../../helper/helper";
import { useMutation } from "@apollo/client";
import { generateVirtualAccount } from "../../api/orgs";
import { OrganisationModel } from "../../models/organisation/OrganisationModelV2";

type Props = {
    onBankPicked?: (bank: BankType) => void
    availableBanks: BankType[]
    isOpen: boolean
    onClose: () => void
    onRefetch?: () => void;
    org: OrganisationModel
    mode: 'create' | 'view' | 'select'
}

export const ModalPickVA: React.FC<Props> = ({ onBankPicked, availableBanks, onClose, isOpen, onRefetch, org, mode }) => {

    const [selectedBank, setSelectedBank] = useState<BankType | undefined>(undefined);

    // GraphQL mutation
    const [createVirtualAccount, { loading }] = useMutation(generateVirtualAccount, {
        onCompleted: (data) => {
            showSuccessMessage(t('va_created_successfully') || 'Virtual Account created successfully');
            onClose()
            if (onRefetch) onRefetch();
        },
        onError: (error) => {
            showErrorMessage(error.message || (t('failed_to_create_va') || 'Failed to create Virtual Account'));
        }
    });


    const handleAddVA = async () => {
        try {
            const selectedProvider = org.payments.paymentProviders.find(provider =>
                (provider.displayName || provider.legalName) === selectedBank?.name
            );
            const providerId = selectedProvider ? selectedProvider.id : null;

            if (!providerId || !org?.organisation?.id) {
                showErrorMessage(t('invalid_selection') || 'Invalid bank selection or organization');
                return;
            }

            await createVirtualAccount({
                variables: {
                    provider_id: providerId,
                    mill_id: org.organisation.id
                }
            });
        } catch (err) {
            console.error('Error creating VA:', err);
        }
    };

    const handleSelectVa = () => {
        onBankPicked?.(selectedBank)
        onClose();
    }

    const onValidate = () => {
        switch (mode) {
            case 'create': handleAddVA(); break;
            case 'select': handleSelectVa(); break;
            case 'view': onClose(); break;
        }
    }

    const buttonLabel = (): string => {
        switch (mode) {
            case 'create': return loading ? t('creating') : t('create_va');
            case 'select': return t('select');
            case 'view': return t('close');
        }
    }

    const isViewing = ():boolean => {
        return mode === 'view'
    }

    const canValidate = () => {
        if (isViewing()) {
            return true
        }
        return !isNullOrUndefined(selectedBank) && !loading && !isArrayNullOrEmpty(availableBanks)
    }

    return (<Dialog open={isOpen} onClose={() => { onClose(); }} maxWidth="sm" fullWidth>
        <DialogContent className="pem-modal-content">
            <div className="pem-modal-inner">
                <h3 className="pem-modal-title">
                    {t('create_virtual_account')}
                </h3>

                <p className="pem-modal-subtitle">
                    {t('select_bank_for_va')}
                </p>

                <div className="pem-bank-list">
                    {!isArrayNullOrEmpty(availableBanks) ? (
                        availableBanks.map((bank) => (
                            <div
                                key={bank.code}
                                onClick={() => setSelectedBank(selectedBank?.id === bank.id ? undefined : bank)}
                                className={`pem-bank-item ${selectedBank?.id === bank.id ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                                style={{
                                    '--color-primary': colorPrimary()
                                } as React.CSSProperties}
                            >
                                <div className={`pem-radio ${selectedBank?.id === bank.id ? 'selected' : ''}`}>
                                    {selectedBank?.id === bank.id && (
                                        <span className="pem-radio-check">✓</span>
                                    )}
                                </div>

                                <RenderBankLogo bankNameOrAccount={bank.name} size="large" paymentProviders={org.payments.paymentProviders} />

                                <div className="pem-bank-info">
                                    <div className="pem-bank-name">
                                        {bank.name}
                                    </div>
                                    <div className="pem-bank-type">
                                        Open VA
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="pem-empty-state">
                            {t('no_banks_available') || 'No banks available for VA creation'}
                        </div>
                    )}
                </div>

                <div className="pem-actions">
                    {
                        mode !== 'view' &&
                        <Button
                            onClick={() => { onClose() }}
                            disabled={loading}
                            style={{
                                color: '#666',
                                textTransform: 'none'
                            }}
                        >
                            {t('cancel') || 'Cancel'}
                        </Button>

                    }
                    <Button
                        onClick={onValidate}
                        disabled={!canValidate()}
                        variant="contained"
                        style={{
                            backgroundColor: canValidate()
                                ? colorPrimary()
                                : '#ccc',
                            color: 'white',
                            textTransform: 'none',
                            boxShadow: canValidate()
                                ? '0 4px 12px rgba(0, 151, 167, 0.3)'
                                : 'none'
                        }}
                    >
                        {buttonLabel()}
                    </Button>
                </div>
            </div>
        </DialogContent>
    </Dialog>)
}